import { useMemo, useState } from "react";
import { Button, Card, Flex, Space, Tooltip, Typography } from "antd";
import { SidebarSimpleIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { colors, radii } from "@libertify/ui/theme-v2";
import { useInsights } from "../../hooks/useInsights";
import { useMissingProjectTitles } from "../../hooks/useMissingProjectTitles";
import { getInsightProjectIds } from "../../helpers/insights";
import { InsightDetailModal } from "./InsightDetailModal";
import { LoadingInsights } from "./LoadingInsights";
import { InsightCardList } from "./InsightCardList";
import { useAntTheme } from "hooks/useAntTheme";

const NO_PROJECT_IDS: string[] = [];
const NO_PROJECT_TITLES: Record<string, string> = {};

export type InsightsPanelProps = {
  organizationId: string;
  workspaceId: string | undefined;
  projectTitleById?: Record<string, string>;
  // Shows this project's insights instead of the organization's
  projectId?: string;
};

export const InsightsPanel = ({
  organizationId,
  workspaceId,
  projectTitleById = NO_PROJECT_TITLES,
  projectId,
}: InsightsPanelProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedInsightId, setSelectedInsightId] = useState<string | null>(
    null
  );

  const {
    insights,
    status,
    isLoading,
    isRefreshing,
    refresh,
    updateTaskState,
  } = useInsights(organizationId, workspaceId, projectId);

  const selectedInsight =
    insights.find((insight) => insight.id === selectedInsightId) ?? null;

  // Project insights never link to other projects, so no titles to fetch
  const insightProjectIds = useMemo(
    () => (projectId ? NO_PROJECT_IDS : insights.flatMap(getInsightProjectIds)),
    [projectId, insights]
  );
  const resolvedProjectTitleById = useMissingProjectTitles(
    organizationId,
    insightProjectIds,
    projectTitleById
  );

  const isProcessing = status === "New" || status === "Processing";

  if (collapsed) {
    return (
      <StyledPanelWrapper $collapsed>
        <Card
          style={{
            width: "100%",
            height: "100%",
            cursor: "pointer",
            borderRadius: radii.box,
          }}
          styles={{ body: { padding: 8 } }}
          onClick={() => setCollapsed(false)}
        >
          <Flex vertical align="center" gap={12} style={{ height: "100%" }}>
            <Tooltip title={t("media.analytics.insights.expand")}>
              <SidebarSimpleIcon size={16} />
            </Tooltip>
            <Flex
              vertical
              align="center"
              justify="center"
              gap={4}
              style={{ height: "100%" }}
            >
              <Typography.Title
                level={3}
                style={{ margin: 0, color: theme.colorPrimary }}
              >
                {insights.length}
              </Typography.Title>
              <StyledRotatedLabel type="secondary" strong>
                {t("media.analytics.insights.title").toUpperCase()}
              </StyledRotatedLabel>
            </Flex>
          </Flex>
        </Card>
      </StyledPanelWrapper>
    );
  }

  return (
    <StyledPanelWrapper $collapsed={false}>
      <Card
        style={{ flex: 1, minHeight: 0, borderRadius: radii.box }}
        styles={{
          body: { padding: 0, height: "100%" },
          header: { padding: 12 },
        }}
        title={
          <Flex align="center" justify="space-between" gap={4} wrap="wrap">
            <Typography.Text>
              {t("media.analytics.insights.title")}
            </Typography.Text>
            <Space>
              <Button
                icon={<ArrowsClockwiseIcon size={12} />}
                loading={isRefreshing || isProcessing}
                onClick={refresh}
                size="small"
                shape="round"
                style={{ color: colors.accent.base, fontSize: 12 }}
              >
                {t("media.analytics.insights.refresh")}
              </Button>
              <Tooltip title={t("media.analytics.insights.collapse")}>
                <Button
                  type="text"
                  icon={<SidebarSimpleIcon size={16} />}
                  onClick={() => setCollapsed(true)}
                />
              </Tooltip>
            </Space>
          </Flex>
        }
      >
        <LoadingInsights spinning={isProcessing || isLoading}>
          <Flex vertical gap={12} style={{ padding: 12 }}>
            <InsightCardList
              insights={insights}
              status={status}
              isLoading={isLoading}
              setSelectedInsight={(insight) =>
                setSelectedInsightId(insight.id ?? null)
              }
              onComplete={(id) => updateTaskState(id, "Done")}
              onPostpone={(id) => updateTaskState(id, "Snoozed")}
              onDismiss={(id) => updateTaskState(id, "Dismissed")}
            />
          </Flex>
        </LoadingInsights>
      </Card>

      <InsightDetailModal
        key={selectedInsightId ?? "none"}
        open={selectedInsightId !== null}
        insight={selectedInsight}
        organizationId={organizationId}
        projectId={projectId}
        projectTitleById={resolvedProjectTitleById}
        onClose={() => setSelectedInsightId(null)}
        onComplete={(id) => updateTaskState(id, "Done")}
        onPostpone={(id) => updateTaskState(id, "Snoozed")}
        onDismiss={(id) => updateTaskState(id, "Dismissed")}
      />
    </StyledPanelWrapper>
  );
};

// ─── Styled components ───

const StyledPanelWrapper = styled.div<{ $collapsed: boolean }>`
  flex: ${({ $collapsed }) => ($collapsed ? "0 0 40px" : "0 0 20%")};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledRotatedLabel = styled(Typography.Text)`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 10px;
  letter-spacing: 0.08em;
`;
